import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Camera, X, FlipVertical } from 'lucide-react';

const SCANNER_CONTAINER_ID = 'barcode-scanner-container';

function BarcodeScanner({ isOpen, onClose, onScan, continuous = true }) {
  const [error, setError] = useState(null);
  const [lastScanned, setLastScanned] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [activeCameraIndex, setActiveCameraIndex] = useState(0);
  const scannerRef = useRef(null);
  const lastScannedRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        scannerRef.current.clear();
      } catch (e) {
        // Ignore cleanup errors
      }
      scannerRef.current = null;
    }
  }, []);

  const startScanning = useCallback(async (scanner, deviceId) => {
    try {
      await scanner.start(
        deviceId,
        {
          fps: 10,
          qrbox: { width: 280, height: 120 },
          aspectRatio: 1.777,
        },
        (decodedText) => {
          const cleaned = decodedText.replace(/[-\s]/g, '');
          if (!/^\d{10}$|^\d{13}$/.test(cleaned)) return;

          // Debounce: don't re-scan same ISBN within 2 seconds
          if (lastScannedRef.current === cleaned) return;
          lastScannedRef.current = cleaned;

          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = setTimeout(() => {
            lastScannedRef.current = null;
          }, 2000);

          setLastScanned(cleaned);
          onScan(cleaned);

          if (!continuous) {
            scanner.stop().catch(() => {});
            onClose();
          }
        },
        () => {
          // QR scan failures are normal during scanning — ignore
        }
      );
    } catch (err) {
      setError('Failed to start camera: ' + err.message);
    }
  }, [onScan, onClose, continuous]);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;

    const initScanner = async () => {
      try {
        const devices = await Html5Qrcode.getCameras();
        if (!mounted) return;

        if (devices.length === 0) {
          setError('No cameras found on this device.');
          return;
        }

        setCameras(devices);

        // Prefer back camera on mobile
        const backIndex = devices.findIndex(d =>
          d.label.toLowerCase().includes('back') ||
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        const selectedIndex = backIndex >= 0 ? backIndex : 0;
        setActiveCameraIndex(selectedIndex);

        const scanner = new Html5Qrcode(SCANNER_CONTAINER_ID, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.CODE_128,
          ],
        });
        scannerRef.current = scanner;

        await startScanning(scanner, devices[selectedIndex].id);
      } catch (err) {
        if (mounted) {
          setError('Camera access denied. Please allow camera permissions and try again.');
        }
      }
    };

    // Small delay to ensure the DOM container is rendered
    const timer = setTimeout(initScanner, 100);

    return () => {
      mounted = false;
      clearTimeout(timer);
      clearTimeout(debounceTimerRef.current);
      stopScanner();
    };
  }, [isOpen, startScanning, stopScanner]);

  const switchCamera = useCallback(async () => {
    if (cameras.length <= 1) return;

    const nextIndex = (activeCameraIndex + 1) % cameras.length;
    setActiveCameraIndex(nextIndex);

    await stopScanner();

    const scanner = new Html5Qrcode(SCANNER_CONTAINER_ID, {
      formatsToSupport: [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.CODE_128,
      ],
    });
    scannerRef.current = scanner;
    await startScanning(scanner, cameras[nextIndex].id);
  }, [cameras, activeCameraIndex, stopScanner, startScanning]);

  const handleClose = useCallback(async () => {
    await stopScanner();
    setError(null);
    setLastScanned(null);
    lastScannedRef.current = null;
    onClose();
  }, [stopScanner, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b bg-purple-50">
          <h3 className="font-bold text-purple-800 flex items-center gap-2">
            <Camera size={20} />
            Scan ISBN Barcode
          </h3>
          <div className="flex items-center gap-2">
            {cameras.length > 1 && (
              <button
                onClick={switchCamera}
                className="text-purple-600 hover:text-purple-800 p-1.5 rounded-lg hover:bg-purple-100 transition-colors"
                title="Switch camera"
              >
                <FlipVertical size={18} />
              </button>
            )}
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Camera viewfinder */}
        <div className="relative bg-black">
          <div id={SCANNER_CONTAINER_ID} className="w-full" style={{ minHeight: '280px' }} />
        </div>

        {/* Status bar */}
        <div className="px-4 py-3 bg-gray-50 border-t">
          {error ? (
            <p className="text-red-600 text-sm">{error}</p>
          ) : lastScanned ? (
            <div className="flex items-center justify-between">
              <p className="text-green-700 text-sm font-medium">
                Scanned: <span className="font-mono">{lastScanned}</span>
              </p>
              {continuous && (
                <span className="text-xs text-gray-500">Ready for next scan...</span>
              )}
            </div>
          ) : (
            <p className="text-gray-600 text-sm">
              Point camera at book's ISBN barcode...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default BarcodeScanner;
